// src/components/mdx/client/VideoPlayer.client.tsx

'use client'

import { useRef, useState, type ReactNode } from 'react'
import { motion } from "motion/react"
import Image from 'next/image'
import { cn } from '~/lib/utils'
import { usePauseEmbedOnHidden } from '~/lib/hooks/usePauseEmbedOnHidden'
import { InteractiveWrapper } from '~/components/blog/content/InteractiveWrapper'

/**
 * Props for the basic VideoPlayer component
 * @typedef {Object} VideoPlayerProps
 * @property {string} url - The URL of the video to embed
 * @property {string} [title] - Optional title to display above the video
 * @property {'youtube' | 'vimeo' | 'custom'} [provider='youtube'] - Video provider type
 * @property {string} [className] - Additional CSS classes to apply
 */
interface VideoPlayerProps {
  /** The URL of the video to embed */
  url: string
  /** Optional title to display above the video */
  title?: string
  /** Video provider type */
  provider?: 'youtube' | 'vimeo' | 'custom'
  /** Additional CSS classes to apply */
  className?: string
}

/**
 * Props for the enhanced VideoPlayer component with additional features
 * @typedef {Object} EnhancedVideoPlayerProps
 * @extends VideoPlayerProps
 * @property {string} [thumbnail] - Optional thumbnail image URL to show before video loads
 * @property {string} [description] - Optional description text to display below the title
 * @property {boolean} [autoplay=false] - Whether the video should autoplay
 * @property {boolean} [muted=false] - Whether the video should be muted by default
 * @property {boolean} [controls=true] - Whether to show video controls
 * @property {boolean} [loop=false] - Whether the video should loop
 * @property {'video' | 'square' | 'custom'} [aspectRatio='video'] - Aspect ratio of the video player
 * @property {string} [customAspectRatio] - Custom aspect ratio CSS class when aspectRatio is 'custom'
 * @property {ReactNode} [footer] - Optional footer content to display below the video
 * @property {boolean} [isInteractive=true] - Whether to wrap the video in an interactive wrapper
 */
interface EnhancedVideoPlayerProps extends VideoPlayerProps {
  /** Optional thumbnail image URL to show before video loads */
  thumbnail?: string
  /** Optional description text to display below the title */
  description?: string
  /** Whether the video should autoplay */
  autoplay?: boolean
  /** Whether the video should be muted by default */
  muted?: boolean
  /** Whether to show video controls */
  controls?: boolean
  /** Whether the video should loop */
  loop?: boolean
  aspectRatio?: 'video' | 'square' | 'custom'
  customAspectRatio?: string
  footer?: ReactNode
  isInteractive?: boolean
}

/**
 * Helper function to get embed URL for different video providers
 * @param {string} url - The original video URL
 * @param {'youtube' | 'vimeo' | 'custom'} provider - The video provider type
 * @param {Object} [options={}] - Video playback options
 * @param {boolean} [options.autoplay] - Whether the video should autoplay
 * @param {boolean} [options.muted] - Whether the video should be muted
 * @param {boolean} [options.controls] - Whether to show video controls
 * @param {boolean} [options.loop] - Whether the video should loop
 * @returns {string} The embed URL for the video
 */
function getEmbedUrl(url: string, provider: 'youtube' | 'vimeo' | 'custom', options: { autoplay?: boolean; muted?: boolean; controls?: boolean; loop?: boolean } = {}): string {
  if (!url) return ''
  
  if (provider === 'youtube') {
    const regex = /(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^&?]+)/
    const match = regex.exec(url)
    const videoId = match?.[1]
    
    if (!videoId) return url
    
    const params = new URLSearchParams({
      autoplay: options.autoplay ? '1' : '0',
      mute: options.muted ? '1' : '0',
      controls: options.controls ? '1' : '0',
      loop: options.loop ? '1' : '0',
      rel: '0',
      modestbranding: '1'
    })
    
    return `https://www.youtube.com/embed/${videoId}?${params.toString()}`
  }
  
  if (provider === 'vimeo') {
    const regex = /vimeo\.com\/(?:video\/)?([0-9]+)/
    const match = regex.exec(url)
    const videoId = match?.[1]
    
    if (!videoId) return url
    
    const params = new URLSearchParams({
      autoplay: options.autoplay ? '1' : '0',
      muted: options.muted ? '1' : '0',
      controls: options.controls ? '1' : '0',
      loop: options.loop ? '1' : '0',
      title: '0',
      byline: '0',
      portrait: '0'
    })
    
    return `https://player.vimeo.com/video/${videoId}?${params.toString()}`
  }
  
  return url
}

/**
 * Simple video player component with basic functionality
 * @param {VideoPlayerProps} props - The component props
 * @param {string} props.url - The URL of the video to embed
 * @param {string} [props.title] - Optional title to display above the video
 * @param {'youtube' | 'vimeo' | 'custom'} [props.provider='youtube'] - Video provider type
 * @param {string} [props.className] - Additional CSS classes to apply
 * @returns {JSX.Element} The rendered video player component
 */
export function VideoPlayer({
  url,
  title,
  provider = 'youtube',
  className
}: VideoPlayerProps): JSX.Element {
  const embedUrl = getEmbedUrl(url, provider)
  // Cache Components: a YouTube/Vimeo <iframe> keeps playing (with audio) under
  // `display:none`, and there's no `.pause()` for cross-origin embeds — so the
  // hook blanks the frame's `src` while this route is hidden by <Activity> and
  // restores it on return. The `src` is rendered normally below (SSR + no-JS get
  // the embed); the hook only blanks-on-hide / restores-on-show via the ref, and
  // since `embedUrl` is stable React never re-touches `src`, so the imperative
  // writes don't fight reconciliation.
  const iframeRef = useRef<HTMLIFrameElement>(null)
  usePauseEmbedOnHidden(iframeRef, embedUrl)

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className={cn("my-8", className)}
    >
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/50">
        {title && (
          <div className="border-b border-gray-200 px-4 py-3 dark:border-gray-800">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white">
              {title}
            </h3>
          </div>
        )}
        <div className="aspect-[16/9] w-full">
          {/* `src` is rendered for SSR/no-JS; usePauseEmbedOnHidden only blanks it on hide and restores on show. */}
          <iframe
            ref={iframeRef}
            src={embedUrl}
            title={title ?? 'Video player'}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="h-full w-full"
          />
        </div>
      </div>
    </motion.div>
  )
}

/**
 * Enhanced video player component with additional features and customization options
 * @param {EnhancedVideoPlayerProps} props - The component props
 * @param {string} props.url - The URL of the video to embed
 * @param {string} [props.title] - Optional title to display above the video
 * @param {'youtube' | 'vimeo' | 'custom'} [props.provider='youtube'] - Video provider type
 * @param {string} [props.thumbnail] - Optional thumbnail image URL to show before video loads
 * @param {string} [props.description] - Optional description text to display below the title
 * @param {boolean} [props.autoplay=false] - Whether the video should autoplay
 * @param {boolean} [props.muted=false] - Whether the video should be muted by default
 * @param {boolean} [props.controls=true] - Whether to show video controls
 * @param {boolean} [props.loop=false] - Whether the video should loop
 * @param {'video' | 'square' | 'custom'} [props.aspectRatio='video'] - Aspect ratio of the video player
 * @param {string} [props.customAspectRatio] - Custom aspect ratio CSS class when aspectRatio is 'custom'
 * @param {ReactNode} [props.footer] - Optional footer content to display below the video
 * @param {boolean} [props.isInteractive=true] - Whether to wrap the video in an interactive wrapper
 * @param {string} [props.className] - Additional CSS classes to apply
 * @returns {JSX.Element} The rendered enhanced video player component
 */
export function EnhancedVideoPlayer({
  url,
  title,
  provider = 'youtube',
  thumbnail,
  description,
  autoplay = false,
  muted = false,
  controls = true,
  loop = false,
  aspectRatio = 'video',
  customAspectRatio,
  footer,
  isInteractive = true,
  className
}: EnhancedVideoPlayerProps): JSX.Element {
  // Behavior change: the thumbnail's play button previously had an empty
  // onClick (a "would need state management" TODO) — clicking it did nothing.
  // `showVideo` now drives it: clicking the overlay swaps the thumbnail for the
  // embed AND starts playback. We force autoplay+muted ON when the user
  // initiated it (muted is required for browsers to honor programmatic
  // autoplay), so it's genuine one-click play rather than reveal-then-click.
  const [showVideo, setShowVideo] = useState(false)
  const embedUrl = getEmbedUrl(url, provider, { autoplay: autoplay || showVideo, muted: muted || showVideo, controls, loop })
  // Cache Components: blank the embed's src while hidden by <Activity> so a
  // YouTube/Vimeo video doesn't keep playing audio on a route the user left. The
  // `src` is rendered normally (SSR + no-JS get the embed); the hook only
  // blanks-on-hide / restores-on-show via the ref.
  const iframeRef = useRef<HTMLIFrameElement>(null)
  usePauseEmbedOnHidden(iframeRef, embedUrl)

  const aspectRatioClass = {
    video: 'aspect-[16/9]',
    square: 'aspect-[1/1]',
    custom: customAspectRatio
  }

  const content = (
    <div className={cn("my-8", className)}>
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-gray-50 shadow-sm transition-shadow hover:shadow-md dark:border-gray-800 dark:bg-gray-900/50">
        {/* Header */}
        {(title ?? description) && (
          <div className="border-b border-gray-200 px-4 py-3 dark:border-gray-800">
            {title && (
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                {title}
              </h3>
            )}
            {description && (
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                {description}
              </p>
            )}
          </div>
        )}

        {/* Video Player */}
        <div 
          className={cn(
            "w-full",
            aspectRatio === 'custom' ? customAspectRatio : aspectRatioClass[aspectRatio]
          )}
        >
          {thumbnail && !showVideo ? (
            <div className="relative h-full w-full">
              <Image
                src={thumbnail}
                alt={title ?? 'Video thumbnail'}
                fill
                className="object-cover"
              />
              <button
                type="button"
                aria-label="Play video"
                onClick={() => setShowVideo(true)}
                className="absolute inset-0 flex items-center justify-center bg-black/30 transition-colors hover:bg-black/40"
              >
                <svg className="h-16 w-16 text-white" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M8 5v14l11-7z"
                  />
                </svg>
              </button>
            </div>
          ) : (
            // `src` is rendered for SSR/no-JS; usePauseEmbedOnHidden only blanks it on hide and restores on show.
            <iframe
              ref={iframeRef}
              src={embedUrl}
              title={title ?? 'Video player'}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="h-full w-full"
            />
          )}
        </div>

        {/* Footer */}
        {footer && (
          <div className="border-t border-gray-200 px-4 py-3 dark:border-gray-800">
            {footer}
          </div>
        )}
      </div>
    </div>
  )

  return isInteractive ? (
    <InteractiveWrapper>
      {content}
    </InteractiveWrapper>
  ) : content
}
